// ISDP CUSTOM: Bridge between the authenticated Backstage app and the standalone
// public portal bundle (PublicApp), which run as two separate React roots.
//
// The public portal cannot use Backstage's identityApi, so when a signed-in user
// passes through the authenticated app we stash their effective role + entitled
// products in sessionStorage. The public portal reads this to unlock the right
// product tabs for partners/devs/admins. We deliberately store ONLY the role and
// product ids (non-sensitive) — never a token — so this is safe to keep in
// sessionStorage (which is also cleared when the tab closes).
import type { DevHubRole } from '@internal/plugin-isdp-common';

const KEY = 'devhub-portal-session';
// Keep the bridge fresh — a session older than this is ignored.
const MAX_AGE_MS = 12 * 60 * 60 * 1000; // 12h

// Readable, non-sensitive "someone is signed in" marker. index.tsx checks it
// SYNCHRONOUSLY (before React boots) to decide whether a public path should load
// the lightweight anonymous PublicApp or the identity-capable main app. It carries
// no identity — just presence — and lives in localStorage so it's shared across
// tabs. Set when identity resolves; cleared on sign-out or a 401.
const SIGNED_IN_KEY = 'devhub-signed-in';

export function markSignedIn(): void {
  try {
    localStorage.setItem(SIGNED_IN_KEY, '1');
  } catch {
    /* non-fatal */
  }
}

export function clearSignedIn(): void {
  try {
    localStorage.removeItem(SIGNED_IN_KEY);
  } catch {
    /* non-fatal */
  }
  // The portal-session bridge (below) encodes the SAME "someone is signed in" state as this
  // flag, so it must die with the flag. Otherwise the anonymous PublicApp's usePortalIdentity
  // reads the stale bridge on the next page load and shows signed-in chrome ("Sign Out") even
  // though the user just signed out. Every sign-out path already calls clearSignedIn, so
  // clearing the bridge here fixes them all — the partner world, the internal hub, and the
  // standalone anonymous root — in one place.
  clearPortalSession();
}

export function isSignedInFlagSet(): boolean {
  try {
    return localStorage.getItem(SIGNED_IN_KEY) === '1';
  } catch {
    return false;
  }
}

export interface PortalSession {
  role: DevHubRole;
  products: string[];
  ts: number;
}

export function writePortalSession(role: DevHubRole, products: string[]): void {
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ role, products, ts: Date.now() } satisfies PortalSession),
    );
  } catch {
    /* storage may be unavailable (private mode) — non-fatal */
  }
}

/** Drop the portal-session bridge. Called from clearSignedIn (sign-out / 401) so the
 *  anonymous portal never resolves a signed-in identity from a stale bridge. */
export function clearPortalSession(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* non-fatal */
  }
}

export function readPortalSession(): PortalSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PortalSession;
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.role !== 'string') return null;
    if (
      !Array.isArray(parsed.products) ||
      !parsed.products.every(p => typeof p === 'string')
    ) {
      return null;
    }
    if (typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}
